import React, { forwardRef } from 'react';
import type { MenuProps as MuiMenuProps } from '@mui/material';
import { Menu as MuiMenu } from '@mui/material';
import { isEmpty } from 'lodash';

import { reportErrorInProductionOrThrow } from '~/Utils';

/**
 * Explicit Menu props based on actual usage in the codebase.
 *
 * Wraps the `@mui/material` primitive, like the sibling Atomic wrappers (Popover,
 * MenuItem). Keeping the MUI import isolated to this file is what made the v4 -> v5
 * swap a single-file change.
 */
export interface MenuProps {
  /** If `true`, the menu is visible. */
  open: boolean;
  /** The element that the menu anchors to. */
  anchorEl?: MuiMenuProps['anchorEl'];
  /** Callback fired when the component requests to be closed. */
  onClose?: MuiMenuProps['onClose'];
  /** The anchor point on the anchor element. */
  anchorOrigin?: MuiMenuProps['anchorOrigin'];
  /** The transform point on the menu element. */
  transformOrigin?: MuiMenuProps['transformOrigin'];
  /** If `true`, the menu stays mounted in the DOM when closed. */
  keepMounted?: boolean;
  /** If `true`, the scroll lock behaviour is disabled while the menu is open. */
  disableScrollLock?: boolean;
  /** Props applied to the internal `Paper` element. */
  PaperProps?: MuiMenuProps['PaperProps'];
  /** Props applied to the internal `MenuList` element. */
  MenuListProps?: MuiMenuProps['MenuListProps'];
  /** The `id` attribute applied to the root element. */
  id?: string;
  /** CSS class name applied to the root element. */
  className?: string;
  /** Menu contents, normally `MenuItem`s. */
  children?: React.ReactNode;
}

/**
 * This Component is a wrapper on MUI component.
 * This component should be imported and not MUI directly!
 */
// ref type is safely extracted from the underlying MuiMenu component to avoid consumer-side type errors
const Menu = forwardRef<React.ComponentRef<typeof MuiMenu>, MenuProps>(
  (
    {
      open,
      anchorEl,
      onClose,
      anchorOrigin,
      transformOrigin,
      keepMounted,
      disableScrollLock,
      PaperProps,
      MenuListProps,
      id,
      className,
      children,
      ...restProps
    },
    ref
  ) => {
    if (!isEmpty(restProps)) {
      reportErrorInProductionOrThrow(
        `Unexpected props passed to Menu: ${Object.keys(restProps).join(', ')}.
Examine if these need to be removed, or added explicitly to the MenuProps interface and to the component's signature.`,
        true
      );
    }

    return (
      <MuiMenu
        open={open}
        anchorEl={anchorEl}
        onClose={onClose}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        keepMounted={keepMounted}
        disableScrollLock={disableScrollLock}
        PaperProps={PaperProps}
        MenuListProps={MenuListProps}
        id={id}
        className={className}
        ref={ref}
        {...restProps}
      >
        {children}
      </MuiMenu>
    );
  }
);

Menu.displayName = 'Menu';

export default Menu;
